<template lang="pug">
component.uc-animate(
  :data-animate="animate"
  :is="is"
  :style="{ height, zIndex: parallax === 100 ? null : parallax }"
  ref="elRef"
)
  //- .flex-col in case we inherit .flex from parent
  span.w-full.h-full(
    :data-parallax="parallax"
    class="[display:inherit] [flex-direction:inherit]"
    ref="parallaxRef"
  )
    slot
</template>

<script setup lang="ts">
import {
  ref,
  unref,
  watch,
  onMounted,
  onUnmounted,
  watchEffect,
  computed,
  inject
} from 'vue';
import { gsap } from 'gsap';
import SplitText from 'gsap/SplitText';
import TextPlugin from 'gsap/TextPlugin';
import useTheme from '../stores/useTheme';
import validate from '../lib/validateAnimate';
import { regex } from '../lib/validateAnimate';
import createParallax from '../lib/createParallax';
import useSuspend from '../lib/useSuspend';

const audioDoc = inject('audioDoc') as boolean;
const wordDoc = unref<boolean>(inject('wordDoc'));

gsap.registerPlugin(TextPlugin, SplitText);

type Vars = GSAPTweenVars;

const props = withDefaults(
  defineProps<{
    animate?: string | undefined;
    is?: string;
    parallax?: number;
    shouldGrow?: boolean;
  }>(),
  { parallax: 100, is: 'div', animate: undefined, shouldGrow: false }
);

const height = computed(() =>
  props.parallax > 100 ? `${+props.parallax}%` : undefined
);

const elRef = ref<AnimatedElement>();
const parallaxRef = ref<HTMLElement>();
const theme = useTheme();
const suspend = useSuspend();

watchEffect(() => {
  if (parallaxRef.value) {
    createParallax(parallaxRef.value, props.parallax);
  }
});

const createTimeline = () => {
  const el = unref(elRef);

  if (!el) return null;
  if (typeof props.animate !== 'string') return;

  const list = props.animate?.trim().split(/\s+/);
  const is = (s: string) => list.includes(s);

  const timeString = list.find(s => regex.time.test(s));

  const time = timeString
    ? parseFloat(timeString.match(/[\d.]+$/)?.[0] || '1')
    : theme.value['animate-time'] || 1;

  const spinString = list.find(s => regex.spin.test(s));
  const growString = list.find(s => regex.grow.test(s));

  const tl = gsap
    .timeline({ paused: true })
    .fromTo(
      el,
      { opacity: 0, pointerEvents: 'none' },
      {
        opacity: 1,
        ease: 'linear',
        duration: is('scramble') || is('typing') ? 1 : time
      },
      0
    )
    .set(el, { clearProps: true });

  const fromTo = (f: Vars, t: Vars) =>
    tl.fromTo(el, f, { overwrite: 'auto', ...t }, 0);

  const easeX =
    time >= 2
      ? 'power1'
      : `elastic.out(${Math.min(1.5 / Math.sqrt(time), 1.5)}, 0.7)`;
  const easeY =
    time >= 2
      ? 'power1'
      : `elastic.out(${Math.min(1 / Math.sqrt(time), 1)}, 0.7)`;

  const toX: Vars = { x: 0, ease: easeX, duration: time };
  const toY: Vars = { y: 0, ease: easeY, duration: time };
  const toRotateX: Vars = { rotateX: 0, duration: time };
  const toRotateY: Vars = { rotateY: 0, duration: time };
  const toRotateZ: Vars = { rotateZ: 0, duration: time };

  if (is('slide-left')) fromTo({ x: 400 / time }, toX);
  if (is('slide-right')) fromTo({ x: -400 / time }, toX);
  if (is('slide-up')) fromTo({ y: 200 / time }, toY);
  if (is('slide-down')) fromTo({ y: -200 / time }, toY);
  if (is('flip-up')) fromTo({ rotateX: 60 }, toRotateX);
  if (is('flip-down')) fromTo({ rotateX: -60 }, toRotateX);
  if (is('flip-left')) fromTo({ rotateY: -90 }, toRotateY);
  if (is('flip-right')) fromTo({ rotateY: 90 }, toRotateY);
  if (is('blur-in')) {
    fromTo(
      { filter: 'blur(20px)' },
      { filter: 'blur(0px)', duration: time, ease: 'power4.out' }
    );
  }
  if (is('bounce-in')) {
    fromTo(
      { y: -500, opacity: 0, ease: 'bounce-in' },
      { y: 0, opacity: 1, ease: 'bounce.out', duration: time }
    );
  }
  if (is('fly-in')) {
    fromTo(
      { z: 500, opacity: 0, scale: 5 },
      { z: 0, opacity: 1, scale: 1.0, duration: time, ease: 'power4.out' }
    );
  }

  if (is('scramble') || is('typing')) {
    const textElement = el.querySelector('[data-text]');
    const scrambledTextStr = randomString(
      textElement.getAttribute('data-text').length - 1
    );
    const originalText = convertLineBreaks(
      textElement.getAttribute('data-text')
    );
    const scrambledText = is('typing')
      ? ''
      : insertLineBreaks(originalText, scrambledTextStr);

    if (textElement) {
      // Allows for wrapping when the scrambled text appears
      textElement.style.wordWrap = 'break-word';
    }

    tl.fromTo(
      textElement,
      { text: { value: scrambledText, scrambleText: true } },
      { text: { value: originalText, scrambleText: true }, duration: time }
    );
  }

  if (spinString) {
    const dir = spinString.startsWith('-') ? 1 : -1;
    const angle = parseFloat(spinString.match(/[\d.]+$/)?.[0] || '180');

    fromTo({ rotateZ: dir * angle }, toRotateZ);
  }

  if (growString) {
    const dir =
      growString.match(/(left|right|up|down|center)$/)?.[0] || 'center';

    const duration = time;
    const clamp = (v: number) => (v < 0 ? 0 : v > 0.75 ? 0.75 : v);
    const scale = clamp(1 - Math.exp(-time));
    const transformOrigin =
      dir === 'left'
        ? '100% 50%'
        : dir === 'right'
          ? '0% 50%'
          : dir === 'up'
            ? '50% 100%'
            : dir === 'down'
              ? '50% 0%'
              : '50% 50%';

    tl.set(el, { transformOrigin }, 0);

    fromTo(
      { scale },
      {
        duration,
        scale: 1,
        ease: `elastic.out(${Math.min(1 / Math.sqrt(time), 1)}, 0.7)`
      }
    );
  }

  return tl;
};

const init = () => {
  // do not apply animations in word/audio docs
  if (audioDoc || wordDoc) return;

  const el = unref(elRef);
  if (!el) return;

  if (el.timeline) {
    el.timeline.kill();
    el.timeline = null;
  }

  const clearProps = () => {
    gsap.set(el, { clearProps: 'all' });
    gsap.set(el, { zIndex: props.parallax, height: height.value });
  };

  clearProps();

  if (
    (validate(props.animate) || validate()) &&
    !el.closest('.screen--no-fade') &&
    !el.closest('.-authoring-mode') &&
    !suspend.reducedMotion
  ) {
    const tl = createTimeline();

    if (tl) {
      el.reset = () => tl.pause(0);
      el.play = () => tl.play();
      el.timeline = tl;

      if (el.classList.contains('-in')) {
        if (import.meta.env.DEV) {
          // nomralize timescale during HMR
          tl.timeScale(1 / gsap.globalTimeline.timeScale());
          setTimeout(() => tl.timeScale(1), 3e3);
        }

        tl.play(0);
      }

      return;
    }
  }

  const list = props.animate?.trim().split(/\s+/);
  const is = (s: string) => list.includes(s);

  if (suspend.reducedMotion && (is('scramble') || is('typing'))) {
    const textElement = el.querySelector('[data-text]');
    if (textElement) {
      // set the text to the original text if reduced motion is enabled for scramble animations
      textElement.innerHTML = convertLineBreaks(
        textElement.getAttribute('data-text')
      );
    }
  }

  el.reset = () => gsap.set(el, { opacity: suspend.reducedMotion ? 1 : 0 });
  el.play = () => {
    clearProps();
  };
  el.timeline = null;
};

watch(() => props.animate, init, { flush: 'post' });
watch(() => suspend.reducedMotion, init, { flush: 'post' });
onMounted(init);

const randomString = length => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

function insertLineBreaks(originalText, scrambledText) {
  const lines = originalText.split('<br>');
  let currentIndex = 0;
  let result = '';

  for (let i = 0; i < lines.length; i++) {
    const lineLength = lines[i].length;

    result += scrambledText.slice(currentIndex, currentIndex + lineLength);

    if (i < lines.length - 1) {
      result += '<br>';
    }

    currentIndex += lineLength;
  }

  return result;
}

const convertLineBreaks = text => {
  text = text.replace(/\n/g, '<br>');
  if (text.endsWith('<br>')) {
    return convertLineBreaks(text.slice(0, -4));
  } else {
    return text;
  }
};

onMounted(() => {
  document.addEventListener('localeChanged', maybeCallInit);
});

onUnmounted(() => {
  document.removeEventListener('localeChanged', maybeCallInit);
});

const maybeCallInit = event => {
  if (event.detail === 'en-GB') {
    init();
  }
};
</script>
